<template>
    <div class="timer" :class="{ 'timer-linear': layout === 'linear', 'timer-compact': layout === 'compact' }">
        <!-- Linear Layout -->
        <template v-if="layout === 'linear'">
            <div class="timer-item-linear timer-main-linear">
                <div class="timer-progress-background">
                    <div 
                        class="timer-progress-fill" 
                        :style="{ width: progressPercentage + '%' }"
                    ></div>
                </div>
                <div class="timer-content-linear">
                    <v-icon class="timer-icon-linear">mdi-clock-outline</v-icon>
                    <div class="timer-time-linear">{{ formattedTime }}</div>
                </div>
            </div>
            <div class="timer-item-linear">
                <v-icon class="timer-stat-icon-linear">mdi-package-variant</v-icon>
                <div class="timer-value-linear">{{ currentPackstats.count || 0 }}</div>
            </div>
            <div class="timer-item-linear">
                <v-icon class="timer-stat-icon-linear">mdi-chart-line</v-icon>
                <div class="timer-value-linear">{{ formattedAverageTime }}</div>
            </div>
        </template>
        
        <!-- Compact Layout (default) -->
        <template v-else>
            <div class="timer-item timer-main">
                <div class="timer-progress-background">
                    <div 
                        class="timer-progress-fill" 
                        :style="{ width: progressPercentage + '%' }"
                    ></div>
                </div>
                <div class="timer-content">
                    <v-icon class="timer-icon">mdi-clock-outline</v-icon>
                    <div class="timer-time">{{ formattedTime }}</div>
                </div>
            </div>
            <div class="timer-info">
                <div class="timer-item">
                    <v-icon class="timer-stat-icon">mdi-package-variant</v-icon>
                    <div class="timer-value">{{ currentPackstats.count || 0 }}</div>
                </div>
                <div class="timer-item">
                    <v-icon class="timer-stat-icon">mdi-chart-line</v-icon>
                    <div class="timer-value">{{ formattedAverageTime }}</div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'Timer',
    props: {
        currentPackstats: {
            type: Object,
            default: () => ({
                progress: 0,
                targetProgress: 40,
                count: 0,
                time: 0,
                packStart: null
            })
        },
        layout: {
            type: String,
            default: 'compact',
            validator: value => ['linear', 'compact'].includes(value)
        }
    },
    data() {
        return {
            currentTime: Date.now(),
            sessionTimer: null
        }
    },
    computed: {
        sessionTimeElapsed() {
            if (!this.currentPackstats.packStart) return 0;
            
            const now = new Date(this.currentTime);
            const packStart = new Date(this.currentPackstats.packStart);
            return Math.floor((now - packStart) / 1000);
        },
        progressPercentage() {
            const elapsed = this.sessionTimeElapsed;
            const target = this.currentPackstats.targetProgress || 40;
            return Math.min((elapsed / target) * 100, 100);
        },
        formattedTime() {
            const elapsed = this.sessionTimeElapsed;
            const minutes = Math.floor(elapsed / 60);
            const seconds = elapsed % 60;
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        },
        formattedAverageTime() {
            const avgTime = this.currentPackstats.time || 0;
            if (avgTime === 0) return '00:00';
            const minutes = Math.floor(avgTime / 60);
            const seconds = Math.round(avgTime % 60);
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
    },
    methods: {
        startSessionTimer() {
            this.sessionTimer = setInterval(() => {
                this.currentTime = Date.now();
            }, 1000);
        },
        stopSessionTimer() {
            if (this.sessionTimer) {
                clearInterval(this.sessionTimer);
                this.sessionTimer = null;
            }
        }
    },
    mounted() {
        this.startSessionTimer();
    },
    beforeDestroy() {
        this.stopSessionTimer();
    }
}
</script>

<style>
.timer {
    background-color: #f0f0f0;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    min-width: 180px;
}

.timer-item {
    background-color: #fff;
    border-radius: 6px;
    padding: 6px;
    position: relative;
}

.timer-main {
    position: relative;
    overflow: hidden;
}

.timer-progress-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
}

.timer-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
    transition: width 0.3s ease;
}

.timer-content {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 2px 0;
}

.timer-icon {
    font-size: 1.2rem !important;
    color: #333;
}

.timer-time {
    font-size: 1.1rem;
    font-weight: bold;
    color: #000;
    font-family: 'Courier New', monospace;
}

.timer-info {
    display: flex;
    flex-direction: row;
    gap: 4px;
}

.timer-info .timer-item {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-align: center;
}

.timer-stat-icon {
    font-size: 1rem !important;
    color: #666;
}

.timer-value {
    font-size: 1.1rem;
    font-weight: bold;
    color: #000;
}

/* Linear Layout Styles */
.timer-linear {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    background-color: #f0f0f0;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    padding: 8px 12px;
    min-width: auto;
}

.timer-item-linear {
    display: flex;
    align-items: center;
    gap: 6px;
    background-color: #fff;
    border-radius: 6px;
    padding: 6px 8px;
    position: relative;
    white-space: nowrap;
}

.timer-main-linear {
    position: relative;
    overflow: hidden;
    flex: 1;
    min-width: 80px;
}

.timer-content-linear {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.timer-icon-linear {
    font-size: 1rem !important;
    color: #333;
}

.timer-time-linear {
    font-size: 0.9rem;
    font-weight: bold;
    color: #000;
    font-family: 'Courier New', monospace;
}

.timer-stat-icon-linear {
    font-size: 0.9rem !important;
    color: #666;
}

.timer-value-linear {
    font-size: 0.9rem;
    font-weight: bold;
    color: #000;
}

/* Compact Layout Styles (default) */
.timer-compact {
    background-color: #f0f0f0;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    min-width: 180px;
}
</style>